import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Users, MapPin, Briefcase, DollarSign, Play, Pause, Settings, Clock, MessageSquare, Star, CreditCard } from "lucide-react";
import { PersonaDoc } from "@/dao/firestore/PersonasRepository";
import { useTranslation } from "react-i18next";
import { calculateCredits, type ServiceType } from '@/lib/credits';
import { getAudienceSlides } from './AudienceCharts';

interface ScreeningRoomEventModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedPersonas: PersonaDoc[];
  onSubmit: (data: {
    screeningRoomSummary: boolean;
    professionalFeedback: boolean;
    startTime: number;
    endTime: number;
    notes: string;
    language: string;
  }) => void;
  onUpdate?: (data: {
    professionalFeedback: boolean;
    notes: string;
  }) => void;
  videoDuration?: number;
  initialProfessionalFeedback?: boolean;
  initialNotes?: string;
  totalCost?: number;
  canRequest?: boolean;
  insufficientCredits?: boolean;
}

const COLORS = [
  'hsl(var(--primary))', 
  'hsl(var(--brand-accent))', 
  'hsl(var(--brand-warm))', 
  'hsl(var(--success))', 
  'hsl(var(--warning))', 
  'hsl(270 85% 65%)', 
  'hsl(330 85% 65%)', 
  'hsl(45 93% 47%)'
];

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'ca', label: 'Català' },
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
  { value: 'it', label: 'Italiano' },
  { value: 'pt', label: 'Português' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'ru', label: 'Русский' },
  { value: 'zh', label: '中文' },
  { value: 'ja', label: '日本語' },
  { value: 'ko', label: '한국어' },
  { value: 'ar', label: 'العربية' },
  { value: 'hi', label: 'हिन्दी' },
  { value: 'tr', label: 'Türkçe' }
];

export function ScreeningRoomEventModal({ 
  open, 
  onOpenChange, 
  selectedPersonas, 
  onSubmit,
  onUpdate,
  videoDuration = 0,
  initialProfessionalFeedback = false,
  initialNotes = '',
  totalCost = 0,
  canRequest = false,
  insufficientCredits = false
}: ScreeningRoomEventModalProps) {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [screeningRoomSummary, setScreeningRoomSummary] = useState(false);
  const [professionalFeedback, setProfessionalFeedback] = useState(initialProfessionalFeedback);
  const [language, setLanguage] = useState('en');
  const [useFullVideo, setUseFullVideo] = useState(true);
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(videoDuration / 1000); // Convert from milliseconds
  const [notes, setNotes] = useState(initialNotes);
  const [expandedAudience, setExpandedAudience] = useState(false);
  const [expandedCost, setExpandedCost] = useState(false);
  const [hoveredFilter, setHoveredFilter] = useState<{type: string, value: string} | null>(null);
  const [selectedPersonaModal, setSelectedPersonaModal] = useState<PersonaDoc | null>(null);

  // Get audience slides using the getAudienceSlides helper function
  const audienceSlides = getAudienceSlides(selectedPersonas, t, hoveredFilter, setHoveredFilter);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const parseTimeInput = (timeStr: string): number => {
    const parts = timeStr.split(':');
    if (parts.length !== 2) return 0;
    const mins = parseInt(parts[0]) || 0;
    const secs = parseInt(parts[1]) || 0;
    return mins * 60 + secs;
  };

  const getEstimatedCostBreakdown = () => {
    const videoSeconds = useFullVideo 
      ? videoDuration / 1000 
      : Math.max(0, endTime - startTime);
    
    const services: ServiceType[] = professionalFeedback ? ['proAnalysis'] : [];
    return calculateCredits(videoSeconds, selectedPersonas.length, services);
  };

  const calculateEstimatedCost = () => {
    return getEstimatedCostBreakdown().total;
  };

  // Helper function to format income
  const formatIncome = (income: number) => {
    if (income >= 1000000) {
      return `${(income / 1000000).toFixed(1)}M€`;
    } else if (income >= 1000) {
      return `${(income / 1000).toFixed(0)}k€`;
    }
    return `${income}€`;
  };

  const handleSubmit = () => {
    onSubmit({
      screeningRoomSummary,
      professionalFeedback,
      startTime,
      endTime,
      notes,
      language
    });
  };

  // Function to check if a persona matches the current hover filter
  const personaMatchesFilter = (persona: PersonaDoc) => {
    if (!hoveredFilter) return true;
    
    switch (hoveredFilter.type) {
      case 'age':
        const ageRange = hoveredFilter.value;
        const age = persona.age || 0;
        if (ageRange === '18-24') return age >= 18 && age < 25;
        if (ageRange === '25-34') return age >= 25 && age < 35;
        if (ageRange === '35-44') return age >= 35 && age < 45;
        if (ageRange === '45-54') return age >= 45 && age < 55;
        if (ageRange === '55+') return age >= 55;
        break;
      case 'gender':
        return persona.gender === hoveredFilter.value;
      case 'country':
        return persona.location?.country === hoveredFilter.value;
      case 'city':
        return persona.location?.city === hoveredFilter.value;
      case 'income':
        const income = persona.estimated_income_eur || 0;
        const range = hoveredFilter.value;
        if (range === t('audience.incomeRanges.low')) return income < 30000;
        if (range === t('audience.incomeRanges.medium')) return income >= 30000 && income < 50000;
        if (range === t('audience.incomeRanges.high')) return income >= 50000 && income < 80000;
        if (range === t('audience.incomeRanges.veryHigh')) return income >= 80000;
        break;
      case 'occupation':
        const occupations = persona.occupation?.split(/[,/;]/).map(o => o.trim()) || [];
        return occupations.some(occ => 
          occ === hoveredFilter.value || 
          (occ.length > 20 && hoveredFilter.value.includes('...') && occ.startsWith(hoveredFilter.value.replace('...', '')))
        );
      case 'platform':
        return persona.preferred_platforms?.includes(hoveredFilter.value) || false;
      case 'tech':
        const techLevel = persona.technological_proficiency?.split('(')[0].toLowerCase().trim();
        return techLevel === hoveredFilter.value;
      default:
        return true;
    }
    return true;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl h-[95vh] p-0 flex flex-col">
        <DialogHeader className="p-6 pb-4 flex-shrink-0 border-b">
           <DialogTitle className="text-2xl flex items-center gap-3">
             <Play className="h-6 w-6 text-primary" />
             {t('screeningRoom.title')}
            <Badge variant="secondary" className="ml-2">
              {selectedPersonas.length} {selectedPersonas.length === 1 ? t('screeningRoom.personas').slice(0, -1) : t('screeningRoom.personas')}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 h-[calc(95vh-160px)]">
          <div className="p-6 space-y-8">
            {/* Screening Room Audience Section */}
            {selectedPersonas.length > 0 && (
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <Users className="h-6 w-6 text-primary" />
                  <h2 className="text-xl font-semibold">{t('screeningRoom.audienceTitle')}</h2>
                </div>

                <Card>
                  <CardContent className="p-6">
                    {/* One-line Audience Summary */}
                    <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg mb-4">
                      <div className="flex items-center gap-6 text-sm">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-primary" />
                          <span className="font-medium">{selectedPersonas.length} {t('screeningRoom.audienceSummary.personas')}</span>
                        </div>
                         <div className="flex items-center gap-2">
                           <span>{t('screeningRoom.audienceSummary.averageAge')}</span>
                           <span className="font-medium text-primary">
                             {Math.round(selectedPersonas.reduce((sum, p) => sum + (p.age || 0), 0) / selectedPersonas.length)} {t('screeningRoom.audienceSummary.years')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium text-primary">
                            {new Set(selectedPersonas.map(p => p.location?.city).filter(Boolean)).size} {t('screeningRoom.audienceSummary.cities')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium text-primary">
                            {Math.round(selectedPersonas.reduce((sum, p) => sum + (p.estimated_income_eur || 0), 0) / selectedPersonas.length / 1000)}k€ {t('screeningRoom.audienceSummary.averageIncome')}
                          </span>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setExpandedAudience(!expandedAudience)}
                        className="flex items-center gap-2"
                      >
                         {expandedAudience ? (
                           <>
                             <ChevronUp className="h-4 w-4" />
                             {t('screeningRoom.audienceSummary.collapse')}
                           </>
                         ) : (
                           <>
                             <ChevronDown className="h-4 w-4" />
                             {t('screeningRoom.audienceSummary.expand')}
                           </>
                         )}
                      </Button>
                    </div>

                    {/* Detailed Charts - Only shown when expanded */}
                    {expandedAudience && audienceSlides.length > 0 && (
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-medium">{t(audienceSlides[currentSlide].titleKey)}</h3>
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))}
                              disabled={currentSlide === 0}
                            >
                              <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <span className="text-sm text-muted-foreground">
                              {currentSlide + 1} / {audienceSlides.length}
                            </span>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setCurrentSlide(Math.min(audienceSlides.length - 1, currentSlide + 1))}
                              disabled={currentSlide === audienceSlides.length - 1}
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        
                        <div className="bg-card border rounded-lg p-4 min-h-[400px]">
                          {audienceSlides[currentSlide].content}
                          
                          {/* Audience Display */}
                          <div className="mt-6 pt-4 border-t">
                            <h5 className="text-sm font-medium mb-3">{t('screeningRoom.audienceTitle')}</h5>
                            <div className="flex flex-wrap gap-3  overflow-y-auto">
                              {selectedPersonas.map((persona, index) => {
                                const matches = personaMatchesFilter(persona);
                                return (
                                  <div
                                    key={persona.id || index}
                                    className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer hover:bg-muted/50 transition-all duration-200 ${
                                      matches ? 'opacity-100 bg-primary/10 border-primary/20' : 'opacity-10'
                                    }`}
                                    onClick={() => setSelectedPersonaModal(persona)}
                                    title={`Click para ver información de ${persona.name}`}
                                  >
                                    <Avatar className="w-8 h-8">
                                      <AvatarImage src={persona.image || '/placeholder.svg'} alt={persona.name} />
                                      <AvatarFallback className="text-xs">
                                        {persona.name?.charAt(0) || '?'}
                                      </AvatarFallback>
                                    </Avatar>
                                    <span className="text-sm font-medium whitespace-nowrap">{persona.name || 'Unknown'}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </section>
            )}

            {/* Settings Section */}
            <section>
               <div className="flex items-center gap-3 mb-6 mt-4">
                 <Settings className="h-6 w-6 text-primary" />
                 <h2 className="text-xl font-semibold">{t('screeningRoom.settings.title')}</h2>
               </div>

              <Card>
                <CardContent className="p-6 space-y-6">
                  {/* Feedback Options - Only Professional Feedback visible */}
                  <div className="grid grid-cols-1 gap-6">
                     {/* Screening Room Summary - Hidden
                     <div className="flex items-center gap-4" style={{ display: 'none' }}>
                       <Switch 
                         checked={screeningRoomSummary}
                         onCheckedChange={setScreeningRoomSummary}
                       />
                       <div className="space-y-1">
                         <Label className="text-base font-medium">{t('screeningRoom.settings.screeningRoomSummary')}</Label>
                         <p className="text-sm text-muted-foreground">
                           {t('screeningRoom.settings.screeningRoomSummaryDesc')}
                         </p>
                       </div>
                     </div>
                     */}

                     <div className="flex items-center gap-4">
                       <Switch 
                         checked={professionalFeedback}
                         onCheckedChange={(checked) => {
                           setProfessionalFeedback(checked);
                           // Update parent state immediately
                           if (onUpdate) {
                             onUpdate({
                               professionalFeedback: checked,
                               notes
                             });
                           }
                         }}
                       />
                       <div className="flex-1 space-y-1">
                         <Label className="text-base font-medium">{t('screeningRoom.settings.professionalFeedback')}</Label>
                         <p className="text-sm text-muted-foreground">
                           {t('screeningRoom.settings.professionalFeedbackDesc')}
                         </p>
                       </div>
                       <div className="flex flex-col gap-2 min-w-[140px]">
                         <Label className="text-sm font-medium">{t('screeningRoom.settings.language')}</Label>
                         <Select value={language} onValueChange={setLanguage}>
                           <SelectTrigger className="w-full">
                             <SelectValue />
                           </SelectTrigger>
                           <SelectContent>
                             {LANGUAGES.map((lang) => (
                               <SelectItem key={lang.value} value={lang.value}>
                                 {t(`languages.${lang.value}`)}
                               </SelectItem>
                             ))}
                           </SelectContent>
                         </Select>
                       </div>
                     </div>
                  </div>

                  <Separator />

                  {/* Video Timeline - Hidden
                  <div className="space-y-4" style={{ display: 'none' }}>
                     <div className="flex items-center justify-between">
                       <Label className="text-base font-medium">{t('screeningRoom.settings.videoSegment')}</Label>
                       <div className="flex items-center gap-2">
                         <Switch 
                           checked={useFullVideo}
                           onCheckedChange={(checked) => {
                             setUseFullVideo(checked);
                             if (checked) {
                               setStartTime(0);
                               setEndTime(videoDuration / 1000); // Convert from milliseconds
                             }
                           }}
                         />
                         <Label className="text-sm">{t('screeningRoom.settings.fullVideo')}</Label>
                       </div>
                     </div>
                    
                    {!useFullVideo && (
                      <div className="grid grid-cols-2 gap-4">
                         <div className="space-y-2">
                           <Label className="text-sm">{t('screeningRoom.settings.start')}</Label>
                           <Input
                             type="text"
                             pattern="\d+:\d{2}"
                             placeholder="0:00"
                             value={formatTime(startTime)}
                             onChange={(e) => {
                               const seconds = parseTimeInput(e.target.value);
                               if (seconds <= videoDuration / 1000) {
                                 setStartTime(seconds);
                               }
                             }}
                           />
                         </div>
                         <div className="space-y-2">
                           <Label className="text-sm">{t('screeningRoom.settings.end')}</Label>
                           <Input
                             type="text"
                             pattern="\d+:\d{2}"
                             placeholder={formatTime(videoDuration / 1000)}
                             value={formatTime(endTime)}
                             onChange={(e) => {
                               const seconds = parseTimeInput(e.target.value);
                               if (seconds >= startTime && seconds <= videoDuration / 1000) {
                                 setEndTime(seconds);
                               }
                             }}
                           />
                         </div>
                      </div>
                    )}
                    
                     {useFullVideo && (
                       <div className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-lg">
                         {t('screeningRoom.settings.fullVideoInfo', {
                           start: formatTime(0),
                           end: formatTime(videoDuration / 1000),
                           duration: formatTime(videoDuration / 1000)
                         })}
                       </div>
                     )}
                  </div>
                  */}

                  <Separator />

                  {/* Notes */}
                  <div className="space-y-2">
                     <Label className="text-base font-medium">{t('screeningRoom.settings.notes')}</Label>
                     <Textarea
                       placeholder={t('screeningRoom.settings.notesPlaceholder')}
                       value={notes}
                       onChange={(e) => {
                         setNotes(e.target.value);
                         // Update parent state immediately when notes change
                         if (onUpdate) {
                           onUpdate({
                             professionalFeedback,
                             notes: e.target.value
                           });
                         }
                       }}
                       rows={4}
                     />
                     <p className="text-xs text-muted-foreground">
                       {t('screeningRoom.settings.notesFooter')}
                     </p>
                  </div>
                </CardContent>
              </Card>
            </section>

          </div>
        </ScrollArea>

        {/* Fixed Footer with Cost and Actions */}
        <div className="flex-shrink-0 border-t bg-background">
          {/* Cost Estimation */}
          <div className="px-6 py-4 border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CreditCard className="h-5 w-5 text-primary" />
                 <span className="font-medium">{t('screeningRoom.cost.estimated')} </span>
                 <span className={`text-lg font-semibold ${insufficientCredits ? 'text-destructive' : 'text-primary'}`}>
                   {totalCost.toFixed(0) || calculateEstimatedCost().toFixed(0)} {t('screeningRoom.cost.credits')}
                 </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpandedCost(!expandedCost)}
                className="flex items-center gap-2"
              >
                 {expandedCost ? (
                   <>
                     <ChevronUp className="h-4 w-4" />
                     {t('screeningRoom.cost.collapse')}
                   </>
                 ) : (
                   <>
                     <ChevronDown className="h-4 w-4" />
                     {t('screeningRoom.cost.expand')}
                   </>
                 )}
              </Button>
            </div>
            
            {expandedCost && (
              <div className="mt-4 space-y-3 bg-muted/30 p-4 rounded-lg">
                {getEstimatedCostBreakdown().detail.map((item, index) => (
                  item.total > 0 && (
                    <div key={index} className="flex items-center justify-between">
                      <span>{t(item.name)}</span>
                      <span className="font-medium">{Math.round(item.total)} {t('screeningRoom.cost.credits')}</span>
                    </div>
                  )
                ))}
                
                <Separator />
                
                <div className="flex items-center justify-between text-lg font-semibold">
                   <span>{t('screeningRoom.cost.total')}</span>
                   <span className="text-primary">{Math.round(calculateEstimatedCost())} {t('screeningRoom.cost.credits')}</span>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="p-6 space-y-3">
            {insufficientCredits && (
              <div className="text-xs text-destructive text-center">
                {t('studio.critics.insufficientCreditsShort')}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                {t('screeningRoom.actions.cancel')}
              </Button>
              <Button 
                onClick={handleSubmit} 
                className="min-w-[120px]"
                disabled={!canRequest || insufficientCredits}
              >
                {t('screeningRoom.actions.start')}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>

      {/* Persona Detail Modal - Using existing modal design */}
      {selectedPersonaModal && (
        <Dialog open={!!selectedPersonaModal} onOpenChange={() => setSelectedPersonaModal(null)}>
          <DialogContent className="sm:max-w-3xl rounded-xl border shadow-2xl bg-background">
            <div className="space-y-6">
              {/* Header with larger square photo */}
              <div className="flex items-start gap-4">
                <div className="h-32 w-32 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                  <img 
                    src={selectedPersonaModal.image || '/placeholder.svg'} 
                    alt={selectedPersonaModal.name} 
                    className="h-full w-full object-cover" 
                    loading="lazy" 
                  />
                </div>
                <div className="flex-1 space-y-2">
                  <h3 className="text-xl font-semibold">
                    {selectedPersonaModal.name}
                    {selectedPersonaModal.filter_tag === "Famous directors" && (
                      <span className="ml-2 font-normal text-muted-foreground">{t('studio.critics.inTheStyleOf')}</span>
                    )}
                  </h3>
                  <div className="text-muted-foreground space-y-1">
                    <div className="flex items-center gap-2">
                      <span>{selectedPersonaModal.age} {t('studio.critics.years')}</span>
                      <span>•</span>
                      <span>{selectedPersonaModal.gender || t('studio.critics.notAvailable')}</span>
                    </div>
                    {selectedPersonaModal.location && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span>
                          {selectedPersonaModal.location.country || t('studio.critics.countryNotSpecified')}
                          {selectedPersonaModal.location.city && `, ${selectedPersonaModal.location.city}`}
                        </span>
                      </div>
                    )}
                    {selectedPersonaModal.occupation && (
                      <div className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4" />
                        <span>{selectedPersonaModal.occupation}</span>
                      </div>
                    )}
                    {selectedPersonaModal.estimated_income_eur && (
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4" />
                        <span>{formatIncome(selectedPersonaModal.estimated_income_eur)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              {selectedPersonaModal.description && (
                <div className="space-y-2">
                  <h4 className="font-semibold">{t('studio.critics.description')}</h4>
                  <p className="text-muted-foreground leading-relaxed">{selectedPersonaModal.description}</p>
                </div>
              )}

              {/* Professional info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h4 className="font-semibold">{t('studio.critics.personalInfo')}</h4>
                  <div className="text-sm text-muted-foreground space-y-1">
                    {selectedPersonaModal.primary_language && (
                      <div className="flex justify-between">
                        <span>{t('studio.critics.language')}:</span>
                        <span>{selectedPersonaModal.primary_language}</span>
                      </div>
                    )}
                     {selectedPersonaModal.technological_proficiency && (
                       <div className="flex justify-between">
                         <span>{t('studio.critics.techLevel')}:</span>
                         <span>{selectedPersonaModal.technological_proficiency}</span>
                       </div>
                     )}
                  </div>
                </div>
              </div>

              {/* Interests */}
              {selectedPersonaModal.interests && selectedPersonaModal.interests.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-semibold">{t('studio.critics.interests')}</h4>
                  <div className="flex flex-wrap gap-1">
                    {selectedPersonaModal.interests.map((interest, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {interest}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Subculture */}
              {selectedPersonaModal.subculture && (
                <div className="space-y-2">
                  <h4 className="font-semibold">{t('studio.critics.subculture')}</h4>
                  <p className="text-sm text-muted-foreground">{selectedPersonaModal.subculture}</p>
                </div>
              )}

              {/* Preferred Platforms */}
              {selectedPersonaModal.preferred_platforms && selectedPersonaModal.preferred_platforms.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-semibold">{t('studio.critics.preferredPlatforms')}</h4>
                  <div className="flex flex-wrap gap-1">
                    {selectedPersonaModal.preferred_platforms.map((platform, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {platform}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}